/**
 * Plugins-toggle sagas — async logic for plugin enable/disable.
 */

import { call, put, takeLatest, takeEvery } from 'redux-saga/effects';
import * as api from '@/infra/api/client';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  setPlugins,
  replacePluginManifest,
  setPluginsLoading,
  setPluginsError,
  fetchPlugins,
  FETCH_PLUGINS,
  TOGGLE_PLUGIN,
} from './ducks';
import { PluginInfo, PluginManifest } from '@/infra/types/domain';
import { pluginRegistry } from '@/infra/plugin-registry';
import { loadPlugin, unloadPlugin } from '@/infra/store';

// ─── Fetch Plugins ─────────────────────────────────────────

export function* fetchPluginsSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'plugins.fetch', ['pluginsToggle'], action, function* () {
    yield put(setPluginsLoading(true));
    try {
      const plugins = (yield call(api.getPlugins)) as PluginInfo[];
      yield* emitSagaPoint('api_getPlugins_ok', { count: plugins.length });
      yield put(setPlugins(plugins));
      // Sync registry + store with server truth. register() seeds enabledSet
      // from enabled_by_default, so without this the registry drifts from
      // Redux: Admin reads Redux (correct), but gated UI reads the registry
      // (stale), and disabled plugins reappear after refresh.
      for (const p of plugins) {
        const wasEnabled = pluginRegistry.isEnabled(p.id);
        if (p.enabled === wasEnabled) continue;
        pluginRegistry.setEnabled(p.id, p.enabled);
        const manifest = pluginRegistry.getPlugin(p.id);
        if (!manifest) continue;
        if (p.enabled) {
          loadPlugin(
            manifest.id,
            manifest.reducer as ((state: any, action: any) => any) | undefined,
            manifest.saga as (() => Generator) | undefined,
          );
        } else {
          unloadPlugin(manifest.id);
        }
      }
      yield put(setPluginsError(null));
    } catch (error: any) {
      yield put(setPluginsError(error.message || 'Failed to fetch plugins'));
      yield* emitSagaPoint('api_getPlugins_err', { message: error?.message });
    } finally {
      yield put(setPluginsLoading(false));
    }
  });
}

// ─── Toggle Plugin ─────────────────────────────────────────

export function* togglePluginSaga(action: {
  type: string;
  payload: { id: string; enabled: boolean };
}): Generator {
  yield call(withSagaFlow, 'plugins.toggle', ['pluginsToggle'], action, function* () {
    const { id, enabled } = action.payload;
    try {
      // Pext-style: the RPC body carries the post-toggle PluginManifest.
      // Replay it into the slice via replacePluginManifest so description /
      // icon / display_name / todo_types / enabled all converge in one
      // dispatch — no separate ENABLE_PLUGIN / DISABLE_PLUGIN bool-flip
      // and no refetch.
      let manifest: PluginManifest;
      if (enabled) {
        manifest = (yield call(api.enablePlugin, id)) as PluginManifest;
        yield* emitSagaPoint('api_enablePlugin_ok');
        yield put(replacePluginManifest(manifest));
        pluginRegistry.setEnabled(id, true);
        const plugin = pluginRegistry.getPlugin(id);
        if (plugin) {
          loadPlugin(
            plugin.id,
            plugin.reducer as ((state: any, action: any) => any) | undefined,
            plugin.saga as (() => Generator) | undefined
          );
        }
      } else {
        manifest = (yield call(api.disablePlugin, id)) as PluginManifest;
        yield* emitSagaPoint('api_disablePlugin_ok');
        yield put(replacePluginManifest(manifest));
        pluginRegistry.setEnabled(id, false);
        unloadPlugin(id);
      }
      yield put(setPluginsError(null));
    } catch (error: any) {
      yield put(setPluginsError(error.message || 'Failed to toggle plugin'));
      yield* emitSagaPoint('api_togglePlugin_err', { message: error?.message });
    }
  });
}

// ─── Bootstrap on auth ─────────────────────────────────────

// Without this, fetchPlugins() only runs when the user visits Admin Settings
// (AdminSettings.tsx). On every other code path the registry keeps whatever
// the enabled_by_default cache said at module load — so disabled plugins
// silently reappear on refresh.
function* bootstrapPluginsOnAuth(): Generator {
  yield put(fetchPlugins());
}

// ─── Root Plugins Saga ─────────────────────────────────────

export function* pluginsToggleSaga(): Generator {
  yield takeLatest(FETCH_PLUGINS, fetchPluginsSaga);
  yield takeEvery(TOGGLE_PLUGIN, togglePluginSaga);
  yield takeLatest('auth/loginSuccess', bootstrapPluginsOnAuth);
}
